/**
 * Credit Control Dashboard - Research-Driven Redesign
 * 
 * Based on extensive user research findings:
 * - Action-focused workflow (56% of users are task-oriented)
 * - Aging debt visibility (90+, 180+, 365+ thresholds)
 * - Promise tracking (75% ranked as top priority)
 * - No duplication between scorecards and risk alerts
 * - Surface automation health
 * - Prominent unallocated payments visibility
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { NavigationMetricCardAlt } from "@/components/NavigationMetricCardAlt";
import {
  RefreshCw,
  Sparkles,
  User,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Info,
  TrendingDown,
  TrendingUp,
  ListTodo,
  Receipt,
  Clock,
  MessageSquare,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDashboardData } from '@/hooks/useDashboardData';
// import { CollectionStrategySlideout } from './collection-strategy-slideout';

export function CreditControlDashboard() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('everyone');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
  const [strategyTab, setStrategyTab] = useState<'all' | 'active' | 'paused'>('all');

  // Get dashboard data
  const {
    todaysPriorities,
    agingPortfolio,
    performanceSnapshot,
    riskAlerts,
    collectionStrategies,
    aiInsights,
    filterOptions,
    refreshData,
  } = useDashboardData(selectedFilter);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    setIsRefreshing(false);
  };

  const handleFilterChange = (filterId: string) => {
    setSelectedFilter(filterId);
    localStorage.setItem('dashboardFilter', filterId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyDetailed = (value: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getFilterLabel = () => {
    if (selectedFilter === 'everyone') return 'Everyone';
    const option = filterOptions.find(f => f.id === selectedFilter);
    return option?.name || 'Everyone';
  };

  const getSectionTitle = (baseTitle: string) => {
    if (selectedFilter === 'everyone') return baseTitle;
    return `${baseTitle} - ${getFilterLabel()}`;
  };

  const filteredStrategies = collectionStrategies.strategies.filter(strategy => {
    if (strategyTab === 'all') return true;
    if (strategyTab === 'active') return strategy.status === 'active';
    if (strategyTab === 'paused') return strategy.status === 'paused';
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      {/* HEADER CONTROLS */}
      <div className="flex items-center justify-between gap-4">
        {/* Refresh Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>

        <div className="flex items-center gap-3">
          {/* AI Insights Button */}
          <Button
            className="gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
            size="sm"
          >
            <Sparkles className="h-4 w-4" />
            AI Insights
            <Badge variant="secondary" className="ml-1 bg-purple-100 text-purple-700">
              {aiInsights.confidence}% conf
            </Badge>
          </Button>

          {/* Everyone Filter Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <User className="h-4 w-4" />
                {getFilterLabel()}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={() => handleFilterChange('everyone')}
                className="gap-2"
              >
                <User className="h-4 w-4" />
                Everyone
                {selectedFilter === 'everyone' && <CheckCircle2 className="h-4 w-4 ml-auto text-green-600" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {filterOptions.map((option) => (
                <DropdownMenuItem
                  key={option.id}
                  onClick={() => handleFilterChange(option.id)}
                  className="gap-2"
                >
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-xs font-medium">
                    {option.initials}
                  </div>
                  {option.name}
                  {selectedFilter === option.id && <CheckCircle2 className="h-4 w-4 ml-auto text-green-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* PAGE HEADER */}
      <div>
        <h1 className="text-3xl font-bold">Credit Control Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Real-time overview of outstanding invoices and collection activities
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {new Date().toLocaleDateString("en-GB", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      </div>

      {/* ROW 1: TODAY'S PRIORITIES - 4 Gateway Scorecards */}
      <div>
        <h2 className="text-xs uppercase font-medium text-muted-foreground mb-3 tracking-wide">
          Today's Priorities
        </h2>
        {todaysPriorities.hasItems ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Collection Queue */}
            {todaysPriorities.collectionQueue.count > 0 && (
              <NavigationMetricCardAlt
                title="COLLECTION QUEUE"
                value={todaysPriorities.collectionQueue.count.toString()}
                icon={ListTodo}
                variant="info"
                onClick={() => navigate('/collections/queue?filter=today')}
              />
            )}

            {/* Unallocated Payments */}
            {todaysPriorities.unallocatedPayments.count > 0 && (
              <NavigationMetricCardAlt
                title="UNALLOCATED PAYMENTS"
                value={todaysPriorities.unallocatedPayments.count.toString()}
                icon={Receipt}
                variant="info"
                onClick={() => navigate('/payments/allocate?status=unallocated')}
              />
            )}

            {/* Promises Due Today */}
            {todaysPriorities.promisesDueToday.count > 0 && (
              <NavigationMetricCardAlt
                title="PROMISES DUE TODAY"
                value={todaysPriorities.promisesDueToday.count.toString()}
                icon={Clock}
                variant="info"
                onClick={() => navigate('/collections/promises?filter=due-and-broken')}
              />
            )}

            {/* Disputes */}
            {todaysPriorities.disputes.count > 0 && (
              <NavigationMetricCardAlt
                title="DISPUTES"
                value={todaysPriorities.disputes.count.toString()}
                icon={MessageSquare}
                variant="info"
                onClick={() => navigate('/disputes?status=requiring-response')}
              />
            )}
          </div>
        ) : (
          <Card className="border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-900 dark:text-green-100">
                    No urgent items requiring attention
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Your workload is clear
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ROW 2: AGING PORTFOLIO + PERFORMANCE SNAPSHOT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aging Portfolio (Left Panel, 50%) */}
        <Card>
          <CardHeader>
            <CardTitle>{getSectionTitle('Aging Portfolio')}</CardTitle>
            <CardDescription>
              {formatCurrency(agingPortfolio.totalOutstanding)} total outstanding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {agingPortfolio.bands.map((band, index) => (
              <Link
                key={index}
                to={`/analytics-reporting/ageing-analysis?filter=${band.filterKey}`}
              >
                <div className="space-y-2 hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{band.label}</span>
                    <span className="font-semibold">
                      {formatCurrency(band.amount)}
                      <span className="text-muted-foreground ml-2">
                        ({band.percentage}%)
                      </span>
                    </span>
                  </div>
                  <Progress value={band.percentage} className="h-2" />
                </div>
              </Link>
            ))}
            <div className="pt-2">
              <Link to="/analytics-reporting/ageing-analysis">
                <Button variant="link" className="h-auto p-0 text-sm">
                  See your aging debt →
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Performance Snapshot (Right Panel, 50%) */}
        <Card>
          <CardHeader>
            <CardTitle>{getSectionTitle('Performance Snapshot')}</CardTitle>
            <CardDescription>Your achievements today</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Today's Wins */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Today's Wins
              </h3>
              <div className="space-y-1">
                <Link to="/payments/receipts?filter=today">
                  <div className="flex items-center justify-between text-sm hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                    <span className="text-muted-foreground">Payments Received</span>
                    <span className="font-semibold text-lg flex items-center gap-1">
                      {performanceSnapshot.todaysWins.paymentsReceived.count} • {formatCurrencyDetailed(performanceSnapshot.todaysWins.paymentsReceived.amount)}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
                <Link to="/collections/promises?filter=kept-today">
                  <div className="flex items-center justify-between text-sm hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                    <span className="text-muted-foreground">Promises Kept</span>
                    <span className="font-semibold text-lg flex items-center gap-1">
                      {performanceSnapshot.todaysWins.promisesKept}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
                <Link to="/collections/queue?filter=resolved-today">
                  <div className="flex items-center justify-between text-sm hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                    <span className="text-muted-foreground">Cases Resolved</span>
                    <span className="font-semibold text-lg flex items-center gap-1">
                      {performanceSnapshot.todaysWins.casesResolved}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </div>
            </div>

            {/* Month-to-Date Performance Header */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-sm mb-3">Month-to-Date Performance</h3>

              {/* Collection Rate */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">Collection Rate</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <div className="space-y-2">
                            <p className="font-semibold">Collection Rate Calculation:</p>
                            <p className="text-sm">
                              Cash collected this month ÷ Total cash due
                            </p>
                            <p className="text-sm">
                              {formatCurrency(performanceSnapshot.monthToDate.collectionRate.collectedAmount)} ÷{' '}
                              {formatCurrency(performanceSnapshot.monthToDate.collectionRate.totalDueAmount)} ={' '}
                              {performanceSnapshot.monthToDate.collectionRate.current}%
                            </p>
                            <p className="text-sm mt-2">
                              Target: {performanceSnapshot.monthToDate.collectionRate.target}% (industry benchmark)
                            </p>
                            <p className="text-sm">
                              Current gap: {performanceSnapshot.monthToDate.collectionRate.pointsBelowTarget} percentage points below target
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <span className="text-3xl font-bold">
                    {performanceSnapshot.monthToDate.collectionRate.current}%
                  </span>
                </div>
                <Progress
                  value={performanceSnapshot.monthToDate.collectionRate.current}
                  className="h-2"
                />
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">
                    Target: {performanceSnapshot.monthToDate.collectionRate.target}%
                  </span>
                  <span className="text-orange-600 dark:text-orange-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {performanceSnapshot.monthToDate.collectionRate.pointsBelowTarget} points below target
                  </span>
                </div>
              </div>

              {/* Key Metrics */}
              <div>
                <h4 className="font-semibold text-sm mb-2">Key Metrics</h4>
                <div className="grid grid-cols-3 gap-3">
                  {/* DSO */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">DSO</p>
                    <p className="text-xl font-bold">
                      {performanceSnapshot.monthToDate.keyMetrics.dso.value}
                      <span className="text-xs font-normal text-muted-foreground ml-1">
                        days
                      </span>
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Target: {performanceSnapshot.monthToDate.keyMetrics.dso.target}
                    </p>
                  </div>

                  {/* Promise Success */}
                  <Link to="/collections/promises">
                    <div className="space-y-1 hover:bg-accent/50 p-2 rounded-lg transition-colors cursor-pointer">
                      <p className="text-xs text-muted-foreground">Promise Success</p>
                      <p className="text-xl font-bold">
                        {performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.percentage}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.kept} kept of{' '}
                        {performanceSnapshot.monthToDate.keyMetrics.promiseSuccess.total} total
                      </p>
                    </div>
                  </Link>

                  {/* Avg Payment Days */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Avg Payment Days</p>
                    <p className="text-xl font-bold">
                      {performanceSnapshot.monthToDate.keyMetrics.avgPaymentDays.current}
                      <span className="text-xs font-normal text-muted-foreground ml-1">
                        days
                      </span>
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                      <TrendingDown className="h-3 w-3" />
                      {performanceSnapshot.monthToDate.keyMetrics.avgPaymentDays.trend.change} days faster
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 3: RISK ALERTS + COLLECTION STRATEGY */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Alerts (Left Panel, 50%) */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <CardTitle>{getSectionTitle('Risk Alerts')}</CardTitle>
            </div>
            <CardDescription>Customer-specific risks requiring investigation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {riskAlerts.length > 0 ? (
              riskAlerts.map((alert) => (
                <Link key={alert.id} to={alert.clickUrl}>
                  <Card
                    className={`cursor-pointer hover:shadow-md transition-shadow ${
                      alert.severity === 'urgent'
                        ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
                        : alert.severity === 'warning'
                        ? 'bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800'
                        : 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800'
                    }`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          {alert.type === 'strategy-inactive' ? (
                            <>
                              <div className="flex items-center gap-2 mb-1">
                                <AlertTriangle className="h-4 w-4 text-orange-600" />
                                <p className="font-semibold text-sm">Collection Strategy Inactive</p>
                              </div>
                              <p className="text-sm font-medium">{alert.primaryIssue}</p>
                              <p className="text-xs text-muted-foreground mt-1">{alert.context}</p>
                            </>
                          ) : (
                            <>
                              <p className="font-semibold text-sm">{alert.customerName}</p>
                              <p className="text-sm font-medium mt-1">{alert.primaryIssue}</p>
                              <p className="text-xs text-muted-foreground mt-1">{alert.context}</p>
                            </>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 flex-shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))
            ) : (
              <Card className="border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-sm text-green-900 dark:text-green-100">
                        No high-priority risks detected
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        All customers are within normal parameters
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {/* Collection Strategy (Right Panel, 50%) */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{getSectionTitle('Collection Strategy')}</CardTitle>
                <CardDescription>Active strategies and performance</CardDescription>
              </div>
              <Link to="/settings/strategies">
                <Button variant="link" className="h-auto p-0 text-sm">
                  See More →
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Donut Chart Placeholder */}
            <div className="flex flex-col items-center justify-center py-6">
              <div className="relative w-48 h-48 rounded-full border-8 border-primary/20 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {formatCurrency(collectionStrategies.totalInActiveCollection)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Total in Active<br />Collection
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-4 border-b">
              <button
                onClick={() => setStrategyTab('all')}
                className={`pb-2 text-sm font-medium transition-colors ${
                  strategyTab === 'all'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStrategyTab('active')}
                className={`pb-2 text-sm font-medium transition-colors ${
                  strategyTab === 'active'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setStrategyTab('paused')}
                className={`pb-2 text-sm font-medium transition-colors ${
                  strategyTab === 'paused'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Paused
              </button>
            </div>

            {/* Strategy List */}
            <div className="space-y-2">
              {filteredStrategies.map((strategy) => (
                <div
                  key={strategy.id}
                  onClick={() => setSelectedStrategyId(strategy.id)}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Badge
                      variant={strategy.status === 'active' ? 'default' : 'secondary'}
                      className={
                        strategy.status === 'active'
                          ? 'bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400'
                      }
                    >
                      {strategy.status.toUpperCase()}
                    </Badge>
                    <span className="font-medium text-sm truncate">{strategy.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">
                      {formatCurrency(strategy.amountInCollection)}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Collection Strategy Slide-out */}
      {/* TODO: Add slideout when ready */}
      {selectedStrategyId && (
        <div>Slideout placeholder for strategy: {selectedStrategyId}</div>
      )}
    </div>
  );
}
